import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    BaseDataService,
    HttpResponsesHandlersFactory,
    IHttpResponseHandlerSettings
} from "@bbwt/modules/data-service";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { UserHelperService } from "@main/users/user-helper.service";
import { Prescription } from "@project/models/prescriptions/prescription";
import { PrescriptionLine } from "@project/models/prescriptions/prescription-line";
import { PrescriptionLineStatus } from "@project/models/prescriptions/prescription-line-status";
import { PrescriptionsService } from "@project/prescription/classes/prescriptions-service";
import { NoteService } from "@project/note-shared/classes/note.service";
import { Note } from "../../models/notes/note";
import { IEpisode } from "@project/models/episodes/episode";
import { PrescriptionOfflineStorage } from "@project/prescription/classes/prescription-offline.storage";
import { PrescriptionLineOfflineStorage } from "./prescription-line-offline.storage";
import { NoteOfflineStorage } from "@project/note-shared/classes/note-offline.storage";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { describer as medicationFieldDescriber } from "@project/medications-shared/classes/medication-field-describer";
import { SrxPrescriptionStatus } from "@project/models/prescriptions/srx-prescription-status";
import { EpisodePrescriptionLine } from "@project/models/episodes/episode-prescription-line";
import { FileDetails } from "@main/file-storage";
import {EpisodeOfflineStorage} from "@project/episode/episode-offline.storage";

@Injectable({
    providedIn: "root"
})
export class PrescriptionLinesService extends BaseDataService {
    entityTitle = "Prescription Lines";
    url = "api/prescriptions/lines";

    constructor(
        http: HttpClient,
        httpHandlersFactory: HttpResponsesHandlersFactory,
        private userHelperService: UserHelperService,
        private prescriptionService: PrescriptionsService,
        private noteService: NoteService,
        private appOnlineStateService: AppOnlineStateService
    ) {
        super(http, httpHandlersFactory);
    }

    async createDraftPrescriptionLine(line: PrescriptionLine, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        responseHandlerSettings = this.getDefaultSuccessResponseHandler(
            "Draft Prescription Line created successfully",
            responseHandlerSettings
        );

        if (this.appOnlineStateService.isAppOnline) {
            return this.handleRequest(
                this.http.post<PrescriptionLine>(`${this.url}/draft`, line),
                this.handlersFactory.getForCreate(this.entityTitle, responseHandlerSettings)
            );
        } else {
            let prescription: Prescription = PrescriptionOfflineStorage.get(line.prescriptionId);
            if (prescription == null) {
                prescription = await this.prescriptionService.createOfflinePrescriptionFromLine(line);
                line.prescriptionId = prescription.id;
            }

            const newLine: PrescriptionLine = PrescriptionLineOfflineStorage.save(line);

            PrescriptionOfflineStorage.addPrescriptionLine(newLine.prescriptionId, newLine);

            return newLine;
        }
    }

    async saveOnline(prescriptionId: string): Promise<Prescription> {
        const prescription = PrescriptionOfflineStorage.get(prescriptionId);
        const prescriptionLines = prescription.prescriptionLines;

        const firstPrescriptionLine: PrescriptionLine = prescriptionLines.splice(0, 1)[0];
        firstPrescriptionLine.id = null;
        firstPrescriptionLine.prescriptionId = null;
        firstPrescriptionLine.episode = prescription.episode;

        const newLine = await this.createDraftPrescriptionLine(firstPrescriptionLine);
        this.noteService.updateLocalNotesPrescriptionId(prescriptionId, newLine.prescriptionId);
        const notes: Note[] = NoteOfflineStorage.getPrescriptionNotes(newLine.prescriptionId);

        await Promise.all([
            ...prescriptionLines.map(line => {
                return this.createDraftPrescriptionLine({
                    ...line,
                    id: null,
                    prescriptionId: newLine.prescriptionId,
                    episode: newLine.episode
                });
            }),
            ...notes.map(n => {
                return this.noteService.saveLocalNoteOnline(n.id, newLine.episodeId);
            })
        ]);

        this.prescriptionService.deleteOfflinePrescription(prescriptionId);
        return await this.prescriptionService.getOutstandingPrescription(newLine.prescriptionId);
    }

    async updateDraftPrescriptionLine(
        line: PrescriptionLine,
        responseHandlerSettings?: IHttpResponseHandlerSettings
    ): Promise<PrescriptionLine> {
        responseHandlerSettings = this.getDefaultSuccessResponseHandler("Draft Prescription Line updated successfully");

        const localLine: PrescriptionLine = PrescriptionLineOfflineStorage.get(line.id);
        if (!localLine) {
            return this.handleRequest<PrescriptionLine>(
                this.http.put<PrescriptionLine>(`${this.url}/draft/${line.id}`, line),
                this.handlersFactory.getForUpdate(this.entityTitle, responseHandlerSettings)
            );
        } else {
            const prescriptionLine: PrescriptionLine = PrescriptionLineOfflineStorage.update(line);
            PrescriptionOfflineStorage.updatePrescriptionLine(prescriptionLine.prescriptionId, prescriptionLine);
            return prescriptionLine;
        }
    }

    updateLocalPrescriptionsLinesEpisode(oldEpisodeId: string, newEpisode: IEpisode): void {
        const prescriptionsLines: PrescriptionLine[] = PrescriptionLineOfflineStorage.getAll().filter(
            p => p.episodeId == oldEpisodeId
        );

        prescriptionsLines.forEach(pl => {
            PrescriptionLineOfflineStorage.update({ ...pl, episodeId: newEpisode.id, episode: newEpisode });
        });
        PrescriptionOfflineStorage.updateEpisode(oldEpisodeId, newEpisode);
    }

    updateLocalPrescriptionLinesEpisode(prescriptionId: string, newEpisode: IEpisode): void {
        const prescriptionsLines: PrescriptionLine[] = PrescriptionLineOfflineStorage.getAll().filter(
            pl => pl.prescriptionId == prescriptionId
        );

        prescriptionsLines.forEach(pl => {
            PrescriptionLineOfflineStorage.update({ ...pl, episodeId: newEpisode.id, episode: newEpisode });
        });
        PrescriptionOfflineStorage.updateEpisode(prescriptionId, newEpisode);
    }

    async deleteDraftPrescriptionLine(
        lineId: string,
        lastLine: boolean,
        responseHandlerSettings: IHttpResponseHandlerSettings
            = this.getDefaultSuccessResponseHandler("Draft Prescription Line deleted successfully")
    ): Promise<any> {
        const localLine: PrescriptionLine = PrescriptionLineOfflineStorage.get(lineId);
        if (!localLine) {
            return this.handleRequest(
                this.http.delete(`${this.url}/draft/${lineId}?lastLine=${lastLine}`),
                this.handlersFactory.getForDelete(this.entityTitle, responseHandlerSettings)
            );
        } else {
            const prescriptionLine: PrescriptionLine = PrescriptionLineOfflineStorage.remove(lineId);

            if (lastLine) {
                this.prescriptionService.deleteOfflinePrescription(prescriptionLine.prescriptionId);
            } else {
                PrescriptionOfflineStorage.removePrescriptionLine(prescriptionLine.prescriptionId, prescriptionLine.id);
            }

            return prescriptionLine;
        }
    }

    async get(id: string, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<PrescriptionLine> {
        const prescriptionLine: PrescriptionLine = PrescriptionLineOfflineStorage.get(id);
        if (!!prescriptionLine) return prescriptionLine;

        return await this.handleRequest<PrescriptionLine>(
            this.http.get<PrescriptionLine>(`${this.url}/${id}`),
            this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings)
        );
    }

    archive(id: string, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<any> {
        responseHandlerSettings = this.getDefaultSuccessResponseHandler("Prescription Line deleted successfully");

        return this.handleRequest(
            this.http.delete(`${this.url}/${id}/archive`),
            this.handlersFactory.getForDelete(this.entityTitle, responseHandlerSettings)
        );
    }

    getDeleteReorderInfo(line: PrescriptionLine): { canDelete: boolean; canReorder: boolean, canCancel: boolean } {
        const canWritePrescription = this.userHelperService.hasPermission(Permissions.WritePrescriptions);
        const ownerOrClinical =
            (this.userHelperService.hasRole(CoreRole.ClinicalAdmin) &&
                this.userHelperService.hasOrganization(line.createdByOrganizationId)) ||
            this.userHelperService.userId == line?.createdByUserId;
        const prescribed = line?.status == PrescriptionLineStatus.Prescribed;

        return {
            canDelete: canWritePrescription && ownerOrClinical && prescribed && !line.productTypeNHS,
            canReorder: canWritePrescription && !line.productTypeNHS,
            canCancel: canWritePrescription && !!line.prescriptionSrxId,
        };
    }

    getEpisodeDeleteReorderInfo(line: EpisodePrescriptionLine, createdByOrganizationId: string): { canDelete: boolean; canReorder: boolean, canCancel: boolean } {
        const canWritePrescription = this.userHelperService.hasPermission(Permissions.WritePrescriptions);
        const ownerOrClinical =
            (this.userHelperService.hasRole(CoreRole.ClinicalAdmin) &&
                this.userHelperService.hasOrganization(createdByOrganizationId)) ||
            this.userHelperService.userId == line?.createdByUserId;
        const prescribed = line?.status == PrescriptionLineStatus.Prescribed;

        return {
            canDelete: canWritePrescription && ownerOrClinical && prescribed && !line.productTypeNHS,
            canReorder: canWritePrescription && !line.productTypeNHS,
            canCancel: canWritePrescription && !!line.prescriptionSrxId,
        };
    }

    generatePrescriptionLineText(prescriptionLine: PrescriptionLine): string {
        var drugName =
            prescriptionLine.productPack && prescriptionLine.productPack.drugName !== null && prescriptionLine.productPack.drugName !== ""
                ? prescriptionLine.productPack.drugName
                : prescriptionLine.drugName;

        const describedFields = [
            drugName,
            medicationFieldDescriber.dose(prescriptionLine.dose, prescriptionLine.doseUom),
            medicationFieldDescriber.interval(prescriptionLine.interval, prescriptionLine.intervalUom),
            prescriptionLine.route,
            medicationFieldDescriber.duration(prescriptionLine.duration, prescriptionLine.durationUom)
        ];

        const quantity = medicationFieldDescriber.quantity(prescriptionLine.quantity, prescriptionLine.quantityUom);
        if (quantity) {
            describedFields.push(`(Quantity: ${quantity})`);
        }

        if (prescriptionLine.specialInstructions) {
            describedFields.push(`(${prescriptionLine.specialInstructions})`);
        }

        const numberOfPacks = medicationFieldDescriber.numberOfPacks(prescriptionLine.numberOfPacks, "pack");
        if (numberOfPacks) {
            describedFields.push(numberOfPacks);
        }

        const line = describedFields.filter(f => !!f).join(", ");

        return line;
    }

    generateEpisodePrescriptionLineText(prescriptionLine: EpisodePrescriptionLine): string {
        var drugName =
            prescriptionLine.productPack && prescriptionLine.productPack.drugName !== null && prescriptionLine.productPack.drugName !== ""
                ? prescriptionLine.productPack.drugName
                : prescriptionLine.drugName;

        const describedFields = [
            drugName,
            medicationFieldDescriber.dose(prescriptionLine.dose, prescriptionLine.doseUom),
            medicationFieldDescriber.interval(prescriptionLine.interval, prescriptionLine.intervalUom),
            prescriptionLine.route,
            medicationFieldDescriber.duration(prescriptionLine.duration, prescriptionLine.durationUom)
        ];

        const quantity = medicationFieldDescriber.quantity(prescriptionLine.quantity, prescriptionLine.quantityUom);
        if (quantity) {
            describedFields.push(`(Quantity: ${quantity})`);
        }

        if (prescriptionLine.specialInstructions) {
            describedFields.push(`(${prescriptionLine.specialInstructions})`);
        }

        const numberOfPacks = medicationFieldDescriber.numberOfPacks(prescriptionLine.numberOfPacks, "pack");
        if (numberOfPacks) {
            describedFields.push(numberOfPacks);
        }

        const line = describedFields.filter(f => !!f).join(", ");

        return line;
    }

    private getDefaultSuccessResponseHandler(msg: string, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        responseHandlerSettings ??= {};
        responseHandlerSettings.successMessage ??= msg;
        return responseHandlerSettings;
    }

     getFileDetailsFromReportHistory(reportHistoryId: string): Promise<FileDetails> {
        return this.handleRequest(this.http.get<FileDetails>(`${this.url}/${reportHistoryId}/get-file-details-from-report`), this.handlersFactory.getDefault());
    }

    updateDrugNameSatinized(lineId: string, drugNameSatinized: string){
        return this.handleRequest(
            this.http.put<void>(`${this.url}/draft/${lineId}/update-drug-name-satinized`, { drugNameSatinized }),
            this.handlersFactory.getForReadAll()
        );
    }
}